// M004-F0636 §4 (P0, E8 batch/backdated upload — WebUI #109 sublane (b),
// WEB-ENV-002) — the pure model + queue engine behind the drawer's upload
// zone. Wire authority: Spec `core-ledger-bookkeeping` 1.2.0, merged Spec !55
// (EnvelopesReceiptsPost operation + `EnvelopeReceiptUploadRequest`): parts
// `file` (one JPEG/PNG/PDF up to 15 MiB), `receipt_id` (client UUID), and the capture pair `captured_at` + `captured_at_origin` which
// the Spec's structural oneOf requires BOTH present or BOTH absent. There is NO
// `target` part: the envelope is named by the URL path (premise corrected in
// WebUI #185; the Spec declares `target` only on the queue upload). The
// `Idempotency-Key` header carries the same value as `receipt_id`
// (ML001-D013 §2.31.4), so a Retry or a post-step-up resume replays the SAME
// identity — an idempotent re-POST changes nothing on disk.
//
// captured_at: the photo's EXIF DateTimeOriginal read client-side from the
// JPEG APP1/TIFF Exif IFD (tag 0x9003), rendered RFC 3339 (the same
// wall-clock-as-UTC convention the WEB-ENV-002 fixtures rely on); a file with
// no EXIF falls back to its lastModified with captured_at_origin 'client'.
// 'intake' is NEVER sent by a client (Spec: it is the Ledger's own stamp on
// the no-capture-pair branch).
//
// Step-up: a STEP_UP_REQUIRED answer (a 401 assurance challenge — the api.js
// interceptor exempts it from the session bounce) PAUSES the queue. The
// selected File objects survive in queue state, the StepUpDialog challenge is
// surfaced once, and on completion the queue resumes WHERE IT LEFT OFF: the
// same items, the same per-file receipt_id/Idempotency-Key, files already
// stored are never re-uploaded.
import { envelopesApi } from '../../services/api';
import { extractEnvelopeError, newIdempotencyKey, sha256Hex } from './envelopeModel';
import { getStepUpChallengeFromError } from '../../utils/stepUp';

export const ENVELOPE_UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
export const UPLOAD_CONCURRENCY = 2;

const EXIF_DATETIME_LENGTH = 20; // "YYYY:MM:DD HH:MM:SS\0"

const readAscii = (view, offset, length) => String.fromCharCode(...Array.from(view.subarray(offset, offset + length)));

const readUint16 = (view, offset, bigEndian) => view.getUint16(offset, !bigEndian);
const readUint32 = (view, offset, bigEndian) => view.getUint32(offset, !bigEndian);

// Walk a TIFF IFD (entries are 12 bytes: tag, type, count, value/offset) and
// return Map<tag, {type, count, valueOffset}> with valueOffset relative to the
// TIFF header start.
function readIfdEntries(view, tiffStart, ifdOffset, bigEndian) {
  const entries = new Map();
  const count = readUint16(view, tiffStart + ifdOffset, bigEndian);
  let entryOffset = tiffStart + ifdOffset + 2;
  for (let i = 0; i < count; i += 1) {
    if (entryOffset + 12 > view.byteLength) return entries;
    const tag = readUint16(view, entryOffset, bigEndian);
    const type = readUint16(view, entryOffset + 2, bigEndian);
    const valueCount = readUint32(view, entryOffset + 4, bigEndian);
    entries.set(tag, { type, count: valueCount, valueOffset: entryOffset + 8 - tiffStart });
    entryOffset += 12;
  }
  return entries;
}

// The raw EXIF DateTimeOriginal string "YYYY:MM:DD HH:MM:SS", or null when the
// bytes carry no parseable EXIF DateTimeOriginal (the caller then uses the
// file time with captured_at_origin 'client').
export function exifDateTimeOriginalRaw(bytes) {
  let view;
  try {
    view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  } catch (cause) {
    return null;
  }
  // The walk tolerates ANY malformed input: a truncated or garbage EXIF
  // segment must degrade to "no EXIF", never throw into the upload flow.
  try {
    return exifWalk(view, bytes);
  } catch (cause) {
    return null;
  }
}

function exifWalk(view, bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(view.buffer);
  if (u8.length < 4 || u8[0] !== 0xff || u8[1] !== 0xd8) return null; // not a JPEG SOI
  let offset = 2;
  while (offset + 4 <= u8.length) {
    if (u8[offset] !== 0xff) return null;
    const marker = u8[offset + 1];
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) { offset += 2; continue; }
    if (marker === 0xda) return null; // start of scan: no EXIF after this
    const segmentLength = view.getUint16(offset + 2);
    if (marker === 0xe1
      && readAscii(u8, offset + 4, 4) === 'Exif'
      && u8[offset + 8] === 0 && u8[offset + 9] === 0) {
      const tiffStart = offset + 10;
      const bigEndian = readAscii(u8, tiffStart, 2) === 'MM';
      const ifd0Offset = readUint32(view, tiffStart + 4, bigEndian);
      const ifd0 = readIfdEntries(view, tiffStart, ifd0Offset, bigEndian);
      const exifPointer = ifd0.get(0x8769);
      if (!exifPointer) return null;
      const exifIfdOffset = readUint32(view, tiffStart + exifPointer.valueOffset, bigEndian);
      const exifIfd = readIfdEntries(view, tiffStart, exifIfdOffset, bigEndian);
      const dto = exifIfd.get(0x9003);
      if (!dto || dto.type !== 2) return null; // 2 = ASCII
      const valueOffset = dto.count <= 4
        ? tiffStart + dto.valueOffset
        : tiffStart + readUint32(view, tiffStart + dto.valueOffset, bigEndian);
      return readAscii(u8, valueOffset, Math.min(dto.count, EXIF_DATETIME_LENGTH)).replace(/\0.*$/, '');
    }
    offset += 2 + segmentLength;
  }
  return null;
}

// EXIF wall clock "YYYY:MM:DD HH:MM:SS" → RFC 3339. EXIF carries no offset;
// the convention (shared with the WEB-ENV-002 evidence fixtures) renders the
// wall clock as UTC.
export function exifTimestampToRfc3339(raw) {
  const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(String(raw || ''));
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  return `${y}-${mo}-${d}T${h}:${mi}:${s}.000Z`;
}

export function exifDateTimeOriginalRfc3339(bytes) {
  const raw = exifDateTimeOriginalRaw(bytes);
  return raw ? exifTimestampToRfc3339(raw) : null;
}

// Spec v1.2.0 EnvelopeReceiptUploadWithCapture.file: one JPEG/PNG/PDF, up to
// 15 MiB (the same multer limits as the org upload route).
export function validateEnvelopeUploadFile(file = {}) {
  const name = String(file.name || '');
  const type = String(file.type || '');
  const size = Number(file.size || 0);
  if (!name) return { ok: false, reason: 'A file without a name cannot be added.' };
  if (size <= 0) return { ok: false, reason: `${name} is empty.` };
  if (size > MAX_UPLOAD_BYTES) return { ok: false, reason: `${name} is larger than the 15 MiB upload limit.` };
  const typeOk = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(type)
    || /\.(jpe?g|png|webp|pdf)$/i.test(name);
  if (!typeOk) return { ok: false, reason: `${name} is not a JPEG, PNG, WebP or PDF.` };
  return {
    ok: true,
    content_type: type || (/\.pdf$/i.test(name) ? 'application/pdf' : /\.webp$/i.test(name) ? 'image/webp' : /\.png$/i.test(name) ? 'image/png' : 'image/jpeg'),
  };
}

// The capture pair for one file: EXIF DateTimeOriginal when the bytes carry
// one (origin 'exif'), else the file's lastModified (origin 'client').
export function capturePairFor(file, bytes) {
  const exif = exifDateTimeOriginalRfc3339(bytes);
  if (exif) return { captured_at: exif, captured_at_origin: 'exif' };
  const fileTime = Number(file.lastModified);
  return {
    captured_at: Number.isFinite(fileTime) ? new Date(fileTime).toISOString() : new Date().toISOString(),
    captured_at_origin: 'client',
  };
}

// The exact multipart body of the envelope upload (Spec v1.2.0). The capture
// pair is both-or-neither — one without the other matches neither oneOf
// branch and is 400 INVALID_INPUT before dispatch, so the builder refuses it
// locally too.
export function buildEnvelopeUploadForm({ file, receiptId, capturedAt, capturedAtOrigin }) {
  if (Boolean(capturedAt) !== Boolean(capturedAtOrigin)) {
    throw new Error('captured_at and captured_at_origin are a both-or-neither pair (Spec v1.2.0).');
  }
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('receipt_id', receiptId);
  // NO `target` part: the route names the envelope by its PATH (`/envelopes/:id/receipts`); Spec declares `target` only on the queue upload and Core
  // reads it only there (WebUI #185 premise correction). The envelope id is the caller's URL, not a body part.
  if (capturedAt) {
    form.append('captured_at', capturedAt);
    form.append('captured_at_origin', capturedAtOrigin);
  }
  return form;
}

// The closed 201 acknowledgement data: {receipt_id, sha256, size, contentType,
// state:'queued'} — byte-identical for both targets (v1.1 response unchanged).
export const uploadAck = (response) => response?.data?.data || response?.data || response || {};

// One real upload through the repo's own API client (the axios transport
// seam is the only thing suites replace — never the Core↔Ledger boundary).
export function uploadEnvelopeReceipt({ orgId, envelopeId, file, receiptId, capturedAt, capturedAtOrigin, onProgress }) {
  const form = buildEnvelopeUploadForm({ file, receiptId, capturedAt, capturedAtOrigin });
  const report = typeof onProgress === 'function'
    ? (event) => { if (event && event.total > 0) onProgress(Math.min(1, event.loaded / event.total)); }
    : undefined;
  return envelopesApi.uploadReceipt(orgId, envelopeId, form, receiptId, report).then(uploadAck);
}

export const UPLOAD_STATES = ['queued', 'uploading', 'stored', 'failed', 'step-up', 'invalid'];

// The batch queue: N files → N independent idempotent uploads, concurrency 2,
// a STEP_UP_REQUIRED pause that keeps the whole selected file set alive, and
// retries/resumes that replay the SAME receipt_id. `upload` is injectable for
// the unit suites (the same seam downloadEnvelopeArchive injects), defaulting
// to the real client path above.
// The bytes of one File. Modern browsers expose Blob.arrayBuffer(); older
// engines (jsdom 16 among them) only offer the FileReader path — try the
// native API first and fall back.
export const readFileBytes = (file) => {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error || new Error(`${file.name || 'The file'} could not be read.`));
    reader.readAsArrayBuffer(file);
  });
};

// WebUI #240: the SHA-256 (lowercase hex) of a file's bytes, or '' when the
// browser offers no crypto.subtle (a non-secure context). '' means "no
// dedupe for this file" — the upload is never blocked by the check.
export const sha256OfBytes = async (bytes) => {
  try {
    return await sha256Hex(bytes);
  } catch (cause) {
    return '';
  }
};

// WebUI #240 review side: the receipt_document_ids whose sha256 is shared with
// another receipt in the same list. A receipt without a sha256 is never grouped.
export function duplicateReceiptIds(receipts) {
  const counts = new Map();
  (receipts || []).forEach((receipt) => {
    if (receipt && receipt.sha256) counts.set(receipt.sha256, (counts.get(receipt.sha256) || 0) + 1);
  });
  return new Set((receipts || [])
    .filter((receipt) => receipt && receipt.sha256 && counts.get(receipt.sha256) > 1)
    .map((receipt) => receipt.receipt_document_id));
}

// M004-F0777 §2.2: where a byte-identical receipt already lives. 'Unsorted' for
// the literal 'unsorted' id or the Unsorted record; the envelope's name when it
// is in the workspace's list; otherwise 'another envelope' (never the raw id).
export const duplicatePlace = (envelopeId, envelopes = []) => {
  if (envelopeId === 'unsorted') return 'Unsorted';
  const record = (Array.isArray(envelopes) ? envelopes : []).find((item) => item?.envelope_id === envelopeId);
  if (!record) return 'another envelope';
  if (record.kind === 'unsorted') return 'Unsorted';
  return record.name || 'another envelope';
};

export function createUploadQueue({
  orgId,
  envelopeId,
  upload = uploadEnvelopeReceipt,
  concurrency = UPLOAD_CONCURRENCY,
  onChange = () => {},
  onStepUp = () => {},
  onSettled = () => {},
  // WebUI #240: sha256 values of receipts already known in the workspace
  // (from receipt reads); a function so the caller always passes the latest.
  knownSha256s = () => [],
  // Called with the file name of each file skipped as a duplicate (no POST, no tile).
  onDuplicate = () => {},
  // M004-F0777 §2.2: maps a duplicate's envelope_id to its place name.
  placeOf = (envelopeId) => duplicatePlace(envelopeId),
}) {
  let items = [];
  let inFlight = 0;
  let paused = false;
  let uid = 0;

  const emit = () => onChange(items.map((item) => ({ ...item })));

  const summary = () => {
    const total = items.filter((item) => item.state !== 'invalid').length;
    const stored = items.filter((item) => item.state === 'stored').length;
    return {
      total,
      stored,
      failed: items.filter((item) => item.state === 'failed').length,
      duplicate: items.filter((item) => item.state === 'duplicate').length,
      waiting: items.filter((item) => ['queued', 'uploading', 'step-up'].includes(item.state)).length,
      invalid: items.filter((item) => item.state === 'invalid').length,
    };
  };

  // The hashes a new file is compared against: the workspace's known receipts,
  // every item still in play (queued, uploading, stored, step-up) and the
  // sha256 each 201 ack carried.
  const knownHashes = () => {
    const known = new Set((knownSha256s() || []).filter(Boolean));
    items.forEach((item) => {
      if (!['failed', 'invalid'].includes(item.state)) {
        if (item.sha256) known.add(item.sha256);
        if (item.ack?.sha256) known.add(item.ack.sha256);
      }
    });
    return known;
  };

  const start = async (item) => {
    item.state = 'uploading';
    item.error = '';
    item.progress = 0;
    emit();
    try {
      const ack = await upload({
        orgId,
        envelopeId,
        file: item.file,
        receiptId: item.receipt_id,
        capturedAt: item.captured_at,
        capturedAtOrigin: item.captured_at_origin,
        onProgress: (fraction) => { item.progress = fraction; emit(); },
      });
      item.progress = 1;
      item.state = 'stored';
      item.ack = ack;
    } catch (cause) {
      const challenge = getStepUpChallengeFromError(cause);
      if (challenge) {
        // The queue pauses on the FIRST challenge; the item keeps its file,
        // its receipt_id and its place. Never a silent re-mint of identity.
        item.state = 'step-up';
        item.error = cause?.response?.data?.message || cause?.message || 'Verification is required to continue.';
        paused = true;
        onStepUp(challenge, item);
      } else if (extractEnvelopeError(cause).status === 409 && extractEnvelopeError(cause).code === 'RECEIPT_DUPLICATE') {
        // M004-F0777 §2.2: a duplicate is not a failure and has no Retry. The
        // text names the place only; never the raw id or the server message.
        item.state = 'duplicate';
        item.error = `Already uploaded (in ${placeOf(cause?.response?.data?.details?.envelope_id)})`;
      } else {
        item.state = 'failed';
        item.error = extractEnvelopeError(cause).message;
      }
    } finally {
      inFlight -= 1;
      onSettled({ ...item }, summary());
      emit();
      pump();
    }
  };

  const pump = () => {
    if (paused) return;
    while (inFlight < concurrency) {
      const next = items.find((item) => item.state === 'queued');
      if (!next) return;
      inFlight += 1;
      start(next);
    }
  };

  return {
    // Validates, mints one receipt identity per file, captures each file's
    // capture pair from its own bytes, then starts pumping.
    async addFiles(files) {
      const list = Array.from(files || []);
      for (const file of list) {
        uid += 1;
        const validation = validateEnvelopeUploadFile(file);
        if (!validation.ok) {
          items.push({
            key: `up-${uid}`, file, name: file.name || `file ${uid}`, receipt_id: '',
            state: 'invalid', error: validation.reason, captured_at: '', captured_at_origin: '', ack: null,
          });
          continue;
        }
        let pair = { captured_at: '', captured_at_origin: '' };
        let bytes = null;
        try {
          // A file whose bytes cannot be read simply carries no EXIF: the
          // honest fallback is its file time (origin 'client').
          bytes = await readFileBytes(file);
          pair = capturePairFor(file, bytes);
        } catch (cause) {
          pair = capturePairFor(file, new Uint8Array(0));
        }
        // WebUI #240: a byte-identical file is never a second receipt. No
        // override; the file is skipped with no POST and no tile.
        const sha256 = bytes ? await sha256OfBytes(bytes) : '';
        if (sha256 && knownHashes().has(sha256)) {
          onDuplicate(file.name || `file ${uid}`);
          continue;
        }
        items.push({
          sha256,
          key: `up-${uid}`,
          file,
          name: file.name,
          receipt_id: newIdempotencyKey(),
          state: 'queued',
          error: '',
          ...pair,
          ack: null,
        });
      }
      emit();
      pump();
      return items.map((item) => ({ ...item }));
    },
    items: () => items.map((item) => ({ ...item })),
    summary,
    isPaused: () => paused,
    // Resume after the step-up challenge was satisfied: the same items, the
    // same receipt identities, nothing re-picked, nothing re-uploaded.
    resume() {
      if (!paused) return;
      paused = false;
      items.forEach((item) => {
        if (item.state === 'step-up') item.state = 'queued';
      });
      emit();
      pump();
    },
    // Retry a failed (or step-up-blocked) item on the SAME receipt_id.
    retry(key) {
      const item = items.find((candidate) => candidate.key === key);
      if (!item || ['uploading', 'stored', 'invalid', 'duplicate'].includes(item.state)) return;
      item.state = 'queued';
      item.error = '';
      paused = false;
      items.forEach((candidate) => {
        if (candidate.state === 'step-up') candidate.state = 'queued';
      });
      emit();
      pump();
    },
    retryAllFailed() {
      paused = false;
      items.forEach((item) => {
        if (item.state === 'failed' || item.state === 'step-up') {
          item.state = 'queued';
          item.error = '';
        }
      });
      emit();
      pump();
    },
  };
}
